import { NativeOnlyAnimatedView } from "@-/dev.improve.haniverse.components.ui.native-only-animated-view";
import {
  PortalContextBridgeContent,
  usePortalContextValues,
} from "@-/dev.improve.haniverse.components.ui.portal-context-bridge";
import { TextClassContext } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import {
  Content,
  type ContentProps,
  type ContentRef,
  Overlay,
  Portal,
} from "@rn-primitives/tooltip";
import { BlurView } from "@-/com.npmjs.expo-blur";
import { Fragment, type RefAttributes } from "react";
import { Platform, StyleSheet, View } from "@-/com.npmjs.react-native";
import { FadeInDown, FadeInUp, FadeOut } from "@-/com.npmjs.react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "@-/com.npmjs.react-native-screens";

const PLATFORM_COMPONENTS = {
  ["ios" as string]: RNFullWindowOverlay,
};
const FullWindowOverlay = PLATFORM_COMPONENTS[Platform.OS] ?? Fragment;

export function TooltipContent({
  children,
  className,
  portalHost,
  side = "top",
  sideOffset = 4,
  ...props
}: ContentProps &
  RefAttributes<ContentRef> & {
    portalHost?: string;
  }) {
  const contextValues = usePortalContextValues();
  return (
    <Portal hostName={portalHost}>
      <PortalContextBridgeContent values={contextValues}>
        <FullWindowOverlay>
          <Overlay style={Platform.select({ native: StyleSheet.absoluteFill })}>
            <NativeOnlyAnimatedView
              entering={
                side === "top"
                  ? FadeInDown.withInitialValues({ transform: [{ translateY: 3 }] }).duration(150)
                  : FadeInUp.withInitialValues({ transform: [{ translateY: -5 }] })
              }
              exiting={FadeOut}
            >
              <TextClassContext value="text-xs text-white">
                <Content
                  className={cn(
                    "z-50 overflow-hidden rounded-xs border border-border/40 bg-transparent",
                    Platform.select({
                      web: cn(
                        "w-fit origin-(--radix-tooltip-content-transform-origin) text-balance animate-in fade-in-0 zoom-in-95",
                        side === "bottom" && "slide-in-from-top-2",
                        side === "left" && "slide-in-from-right-2",
                        side === "right" && "slide-in-from-left-2",
                        side === "top" && "slide-in-from-bottom-2",
                      ),
                    }),
                    className,
                  )}
                  side={side}
                  sideOffset={sideOffset}
                  {...props}
                >
                  <BlurView
                    intensity={50}
                    style={StyleSheet.absoluteFill}
                    tint={Platform.OS === "ios" ? "systemMaterialDark" : "dark"}
                  />
                  <View className="px-3 py-2 sm:py-1.5">{children}</View>
                </Content>
              </TextClassContext>
            </NativeOnlyAnimatedView>
          </Overlay>
        </FullWindowOverlay>
      </PortalContextBridgeContent>
    </Portal>
  );
}
